import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Printer, HeartPulse, MoonStar } from "lucide-react";
import TrackerLogger from "@/components/TrackerLogger";
import { TRACKERS } from "@/lib/trackers";

const ROWS = Array.from({ length: 7 });

const PRINTABLES = {
  "emotional-wellness-tracker": {
    icon: HeartPulse,
    title: "Emotional Wellness Tracker",
    subtitle: "Understanding your emotional patterns helps create personalised coping strategies. Use this diary to identify triggers and track what works best for you.",
    sectionTitle: "Track your emotional changes",
    sectionDesc: "Note the timing and intensity of mood shifts to recognise patterns and potential triggers.",
    col2: "Intensity",
    col3: "Situation & triggers",
    levels: ["Mild", "Notable", "Significant"],
    strategiesTitle: "Emotional Balance Strategies",
    strategiesDesc: "Track which methods help stabilise your mood. Note the combinations that work best for you.",
    strategies: [
      "Practise mindful breathing and meditation for emotional centring.",
      "Engage in gentle movement like walking or stretching.",
      "Connect with understanding friends or support groups.",
      "Maintain a gratitude journal for positive focus.",
      "Schedule regular self care activities.",
      "Establish consistent sleep patterns for emotional stability.",
    ],
    tip: "Combine different approaches throughout your day. Morning meditation, midday walks and evening journaling can create a balanced routine.",
  },
  "sleep-wellness-tracker": {
    icon: MoonStar,
    title: "Sleep Wellness Tracker",
    subtitle: "Keep track of your sleep patterns, as disruptions can intensify menopause symptoms. Recording daily experiences helps uncover triggers and develop strategies for improved rest and wellbeing.",
    sectionTitle: "Track your sleep patterns",
    sectionDesc: "Document your sleep experiences, including any disturbances and their timing. This helps identify patterns and develop personalised solutions for better rest.",
    col2: "Sleep quality",
    col3: "Observation & triggers",
    levels: ["Mild", "Moderate", "Significant"],
    strategiesTitle: "Sleep Enhancement Strategies",
    strategiesDesc: "Record which strategies improve your sleep quality and create your personalised evening routine for optimal rest.",
    strategies: [
      "Establish a consistent bedtime and wake up schedule daily.",
      "Avoid caffeine and heavy meals before bedtime.",
      "Create a calming bedtime ritual (reading, gentle music, meditation).",
      "Practise deep breathing exercises for pre sleep relaxation.",
      "Minimise screen exposure one hour before bed.",
      "Maintain an optimal bedroom temperature and comfort.",
    ],
    tip: "Consider combining multiple strategies throughout your evening routine. Start with one approach and gradually add others to find your ideal sleep supporting combination.",
  },
};

export default function PrintableTracker() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cfg = PRINTABLES[slug];

  if (!cfg) {
    return (
      <div className="min-h-screen bg-app flex flex-col items-center justify-center px-6 text-center" data-testid="printable-notfound">
        <p className="text-ink-soft">This printable isn't available.</p>
        <button onClick={() => navigate("/toolkit")} className="mt-4 text-brand font-semibold">Back to toolkit</button>
      </div>
    );
  }
  const Icon = cfg.icon;

  return (
    <div className="min-h-screen bg-app" data-testid="printable-page">
      <div className="app-frame px-5 pt-6 pb-16 print-area">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-ink-muted mb-4 no-print" data-testid="printable-back">
          <ChevronLeft size={20} /> Back
        </button>

        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-sage flex items-center justify-center mx-auto">
            <Icon size={24} className="text-brand" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-ink mt-4" data-testid="printable-title">{cfg.title}</h1>
          <p className="text-ink-soft text-sm mt-2 max-w-md mx-auto">{cfg.subtitle}</p>
        </div>

        <div className="mt-6">
          <p className="overline-label">{cfg.sectionTitle}</p>
          <p className="text-ink-soft text-sm mt-2">{cfg.sectionDesc}</p>
        </div>

        <div className="mt-5 no-print">
          {TRACKERS[slug] && <TrackerLogger cfg={TRACKERS[slug]} />}
        </div>

        <p className="overline-label mt-8 no-print">Prefer paper?</p>
        <p className="text-ink-soft text-sm mt-1 mb-3 no-print">Print a blank copy to fill in by hand.</p>

        <div className="mt-5 rounded-2xl border-2 border-[var(--brand)] overflow-hidden">
          <div className="grid grid-cols-12 bg-sage px-3 py-3">
            <div className="col-span-4 font-semibold text-ink text-sm">When</div>
            <div className="col-span-3 font-semibold text-ink text-sm">{cfg.col2}</div>
            <div className="col-span-5 font-semibold text-ink text-sm">{cfg.col3}</div>
          </div>
          {ROWS.map((_, r) => (
            <div key={r} className={`grid grid-cols-12 px-3 py-4 ${r % 2 ? "bg-app" : "bg-white"} border-t border-subtle`} data-testid={`printable-row-${r}`}>
              <div className="col-span-4 pr-2 space-y-3 text-sm text-ink-soft">
                <div>Date: <span className="inline-block border-b border-[var(--surface-accent)] w-16 sm:w-24 align-bottom" /></div>
                <div>Time: <span className="inline-block border-b border-[var(--surface-accent)] w-16 sm:w-24 align-bottom" /></div>
              </div>
              <div className="col-span-3 space-y-2">
                {cfg.levels.map((lvl) => (
                  <div key={lvl} className="flex items-center gap-2 text-sm text-ink-soft">
                    <span className="w-4 h-4 rounded border-2 border-[var(--brand)] flex-shrink-0" />
                    {lvl}
                  </div>
                ))}
              </div>
              <div className="col-span-5">
                <div className="h-full min-h-[72px] rounded-lg border border-[var(--surface-accent)]" />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-subtle p-5 bg-white card-shadow">
          <h2 className="font-serif text-lg font-bold text-ink">{cfg.strategiesTitle}</h2>
          <p className="text-ink-soft text-sm mt-1">{cfg.strategiesDesc}</p>
          <ul className="mt-4 space-y-2.5">
            {cfg.strategies.map((s, i) => (
              <li key={i} className="flex gap-2.5 text-ink-soft text-sm" data-testid={`printable-strategy-${i}`}>
                <span className="w-4 h-4 rounded-[4px] border-2 border-[var(--brand)] flex-shrink-0 mt-0.5" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
          <p className="text-ink-soft text-sm italic mt-4">
            <span className="font-semibold text-ink not-italic">Strategy Tip:</span> {cfg.tip}
          </p>
        </div>

        <button
          data-testid="printable-print"
          onClick={() => window.print()}
          className="mt-6 w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 no-print transition-colors"
        >
          <Printer size={16} /> Print / Download
        </button>
        <p className="text-ink-muted text-xs mt-3 text-center no-print">A self tracking tool for your own notes, not a medical record.</p>
      </div>
    </div>
  );
}
